/**
 * The vocabulary the API and the interface share.
 *
 * The wire values are upper snake case, which is what the backend emits and
 * accepts. The labels are here rather than read from the API so a select can
 * be rendered before anything has loaded.
 */
export const POST_TYPES = [
  { value: 'ANNOUNCEMENT', label: 'Annonce' },
  { value: 'QUESTION', label: 'Question' },
  { value: 'GOOD_DEAL', label: 'Bon plan' },
  { value: 'LOST_AND_FOUND', label: 'Objet perdu ou trouvé' },
  { value: 'RECOMMENDATION', label: 'Recommandation' },
];

/**
 * How a type's chip is painted, so the feed is scannable.
 *
 * The pairs live in the theme (`palette.postTypes`) rather than here, because
 * they are colours. What this replaces mapped each type onto a MUI colour
 * name — and `QUESTION: 'primary'` gave questions the colour of the page's
 * primary action, which teaches people to click a label.
 */
export function postTypeSx(type) {
  return (theme) => {
    // Optional chaining, not laziness: a chip rendered under the stock MUI
    // theme — in a unit test, or in a corner that forgot its provider —
    // should come out plain, not throw.
    const look = theme.palette.postTypes?.[type] || {
      bg: theme.palette.surface?.chip || theme.palette.grey[100],
      ink: theme.palette.text.secondary,
    };

    return { bgcolor: look.bg, color: look.ink, fontWeight: 700 };
  };
}

/**
 * Initials for the little round avatar beside an author.
 *
 * Usernames come in both shapes the sign-up form allows — "Marie Lavigne" and
 * "marie.dupont" — so both separators count.
 */
export function authorInitials(author) {
  return String(author || '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * Where a post stands.
 *
 * A post is in the feed the moment it is written; the only other state is
 * taken down after a report, which only its author still sees. The notice is
 * what stops that looking like the post was lost.
 */
export const POST_STATUS = {
  PUBLISHED: { label: 'Publiée', color: 'success', notice: null },
  HIDDEN: {
    label: 'Retirée',
    color: 'error',
    // Addressed to the author: nobody else is shown these rows.
    notice: 'Cette publication a été retirée du fil après un signalement. Vous seul la voyez encore.',
  },
};

/** The reason a moderator gave for taking a post down. */
export function moderationReason(post) {
  return post?.hidden_reason || null;
}

export const REPORT_REASONS = [
  { value: 'SPAM', label: 'Spam ou publicité' },
  { value: 'OFFENSIVE', label: 'Propos insultants ou haineux' },
  { value: 'MISLEADING', label: 'Information trompeuse' },
  { value: 'OFF_TOPIC', label: 'Sans rapport avec le quartier' },
  { value: 'OTHER', label: 'Autre' },
];

/**
 * Turns an API timestamp into a Date.
 *
 * The API sends ISO 8601 with an offset, which `new Date` reads correctly. A
 * bare "Y-m-d H:i:s" is read as UTC, not as the reader's own zone: the server
 * keeps its clock in UTC, and guessing local time made a post written a minute
 * ago read "il y a 2 heures" in Paris.
 */
function parseApiDate(value) {
  const text = String(value).trim().replace(' ', 'T');
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(text);

  return new Date(hasZone ? text : `${text}Z`);
}

/**
 * The day and the month, for the little calendar block beside an event.
 *
 * Shares `parseApiDate` with the rest of this file rather than re-deriving it
 * at each call site: the "no offset means UTC" rule is the kind of thing that
 * gets forgotten in one copy and not the other.
 */
export function eventDayParts(isoString) {
  const date = parseApiDate(isoString);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    day: date.getDate(),
    month: date.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', ''),
  };
}

/** "samedi 13 septembre, 09:00" — no year unless the event is not this one. */
export function formatEventDate(isoString) {
  const date = parseApiDate(isoString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const sameYear = date.getFullYear() === new Date().getFullYear();

  return date.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: sameYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "il y a 3 jours" — a feed reads better in relative time. */
export function formatRelativeDate(value) {
  const date = parseApiDate(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });

  const steps = [
    [60, 'second', 1],
    [3600, 'minute', 60],
    [86400, 'hour', 3600],
    [604800, 'day', 86400],
    [2592000, 'week', 604800],
    [31536000, 'month', 2592000],
  ];

  for (const [limit, unit, divisor] of steps) {
    if (seconds < limit) {
      return formatter.format(-Math.round(seconds / divisor), unit);
    }
  }

  return formatter.format(-Math.round(seconds / 31536000), 'year');
}

/**
 * When an offer runs, said the way somebody would say it out loud.
 *
 * Dates arrive as plain days ("2026-09-19") and are read as such: parsing them
 * as instants would shift them by a timezone and start Saturday's offer on
 * Friday evening for half the readers.
 */
export function formatOfferWindow(startsOn, endsOn) {
  const day = (value) => {
    const [year, month, date] = String(value).split('-').map(Number);

    return new Date(year, month - 1, date);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = day(startsOn);
  const end = day(endsOn);
  const short = { day: 'numeric', month: 'long' };

  if (start > today) {
    return `Du ${start.toLocaleDateString('fr-FR', short)} au ${end.toLocaleDateString('fr-FR', short)}`;
  }

  if (start.getTime() === end.getTime()) {
    return "Aujourd'hui seulement";
  }

  return `Jusqu'au ${end.toLocaleDateString('fr-FR', short)}`;
}
